import { urlSafeBtoa, encodeObject } from '../util/functions';
import memoryStorage from '../util/memoryStorage';
import { authenticationServiceUrl, strongAuthAppId } from '../settings';
import { AuthCallbackState } from '../types';
import { getLanguageSelection } from '../util/state';

let token: string|null = null;

const getToken = (): string|null => token;

const removeToken = (): void => {
  token = null;
};

const authenticated = (): boolean => token ? true : false;

export interface StrongAuthTokenPayload {
  sub: string, // Contains finnish SSN.
  given_name: string,
  family_name: string,
  exp: number
  // Contains more but here we currently only include values we intend to use.
}

const getTokenPayload = (): StrongAuthTokenPayload|null => {
  if (!token) return null;

  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (exception) {
    console.log('Incorrectly structured token.');
    throw exception;
  }
};

// Return the amount of minutes the token is still valid for.
const minutesUntilTokenExpires = (): number => {
  const tokenPayload = getTokenPayload();

  if (!tokenPayload) return 0;

  const now: Date = new Date();
  const expires: Date = new Date(tokenPayload.exp * 1000);

  const validForMS = Number(expires) - Number(now);

  const validForMinutes = validForMS / (60 * 1000);

  if (validForMinutes <= 0) return 0;

  return validForMinutes;
};

const generateCodeVerifier = (): string => {
  const randomBytes = new Uint8Array(64); // 64 bytes = 128 hex characters
  window.crypto.getRandomValues(randomBytes);
  return urlSafeBtoa(Array.from(randomBytes).map(byte => String.fromCharCode(byte)).join(''));
};

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return urlSafeBtoa(Array.from(new Uint8Array(digest)).map(byte => String.fromCharCode(byte)).join(''));
};

const authenticate = async (
  reservationUuid: string,
  bookForOther: boolean
) => {
  const callbackState: AuthCallbackState = {
    reservationUuid,
    bookForOther,
    campaignUuid: memoryStorage.getItem('campaignUuid'),
    language: getLanguageSelection()
  };

  const state = encodeObject(callbackState);

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  localStorage.setItem('codeVerifier', codeVerifier);
  localStorage.setItem('state', state);

  window.location.href =
      `${authenticationServiceUrl}/authorize?application_id=${strongAuthAppId}&pkce_hash=${codeChallenge}&state=${state}`;
};

// Retrieves a token by sending the code verifier saved in localStorage to the strong authentication service.
const exchangeToken = async (
  requestId: string,
  codeVerifier: string
): Promise<boolean> => {
  try {
   const response = await fetch(`${authenticationServiceUrl}/token?request_id=${requestId}&pkce_code_verifier=${codeVerifier}`);

   token = (await response.json()).id_token;

    if (!token) return false;

    return true;

  } catch (exception) {
    console.error(exception);

    alert('Tunnistautuminen epäonnistui.');

    return false;
  }
};

export default {
    authenticate,
    exchangeToken,
    authenticated,
    getToken,
    getTokenPayload,
    removeToken,
    minutesUntilTokenExpires
};